import * as React from 'react';
import { useEffect, useState } from 'react';

import { ChipGroup, Grid } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useLearningGoalData } from 'bundles/onboarding-common';
import {
  BinocularsIcon,
  RocketLaunchIcon,
  ShuffleIcon,
  TrendUpIcon,
} from 'bundles/onboarding-common/assets/LearningGoalIcons';
import OptionPill from 'bundles/onboarding-common/components/OptionPill';
import { LEARNING_GOAL_OPTIONS, type LEARNING_GOAL_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useFormEventingData from 'bundles/onboarding-common/hooks/useFormEventingData';
import { getLearningGoalOptionName } from 'bundles/onboarding-common/utils';

import _t from 'i18n!nls/onboarding-common';

const LearningGoalQuestion = () => {
  const { onChange, setQuestionValue } = useOnboardingQuestionData();
  const { value: savedValue } = useFetchData({
    useQueryData: useLearningGoalData,
    questionIdForLocalStorage: QUESTION_IDS.learningGoal,
  });
  const [value, setValue] = useState<LEARNING_GOAL_OPTIONS_TYPE | null>(null);
  const learningGoalOptionNames = getLearningGoalOptionName();
  const track = useTracker();
  const { formEventData } = useFormEventingData();

  useEffect(() => {
    setValue(savedValue);

    if (savedValue) {
      setQuestionValue({ __typename: QUESTION_IDS.learningGoal, value: savedValue });
    }
  }, [savedValue, setQuestionValue]);

  const handleClick = (learningGoalOption: LEARNING_GOAL_OPTIONS_TYPE) => {
    setValue(learningGoalOption);
    onChange({ __typename: QUESTION_IDS.learningGoal, value: learningGoalOption });
    track('change_radio', {
      form: formEventData,
      radio: {
        id: QUESTION_IDS.learningGoal,
        value: learningGoalOption,
      },
    });
  };

  return (
    <Grid item xs={12}>
      <ChipGroup multiSelect={false} aria-label={_t('Select a learning goal')}>
        <OptionPill
          label={learningGoalOptionNames.LEARNING_GOAL_LAND_NEW_JOB}
          onClick={() => handleClick(LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB)}
          icon={<RocketLaunchIcon />}
          selected={value === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_LAND_NEW_JOB}
          variant="withSvgIcon"
          iconBackgroundColor="var(--cds-color-emphasis-primary-background-strong)"
        />
        <OptionPill
          label={learningGoalOptionNames.LEARNING_GOAL_CHANGE_CAREER}
          onClick={() => handleClick(LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER)}
          icon={<ShuffleIcon />}
          selected={value === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_CHANGE_CAREER}
          variant="withSvgIcon"
          iconBackgroundColor="var(--cds-color-emphasis-primary-background-strong)"
        />
        <OptionPill
          label={learningGoalOptionNames.LEARNING_GOAL_IMPROVE_SKILLS}
          onClick={() => handleClick(LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS)}
          icon={<TrendUpIcon />}
          selected={value === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_IMPROVE_SKILLS}
          variant="withSvgIcon"
          iconBackgroundColor="var(--cds-color-emphasis-primary-background-strong)"
        />
        <OptionPill
          label={learningGoalOptionNames.LEARNING_GOAL_PERSONAL_INTEREST}
          onClick={() => handleClick(LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST)}
          icon={<BinocularsIcon />}
          selected={value === LEARNING_GOAL_OPTIONS.LEARNING_GOAL_PERSONAL_INTEREST}
          variant="withSvgIcon"
          iconBackgroundColor="var(--cds-color-emphasis-primary-background-strong)"
        />
      </ChipGroup>
    </Grid>
  );
};

export default LearningGoalQuestion;
